import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { apiClient } from "../../services/api";
import {
  LeadDispatchLog,
  LeadDispatchLogsResponse,
  LeadDispatchStatus,
} from "../../types";
import LeadDispatchLogDetail from "./LeadDispatchLogDetail";

const STATUS_CONFIG: Record<
  LeadDispatchStatus,
  { label: string; color: "success" | "warning" | "default" | "error" | "info" }
> = {
  dispatched: { label: "Inviato", color: "success" },
  duplicate: { label: "Duplicato", color: "warning" },
  skipped: { label: "Saltato", color: "default" },
  error: { label: "Errore", color: "warning" },
  failed: { label: "Fallito", color: "error" },
};

const PAGE_LIMIT = 20;
type StatusFilter = LeadDispatchStatus | "all";

interface Props {
  ruleId: string;
}

export default function LeadDispatchAuditLog({ ruleId }: Props) {
  const [logs, setLogs] = useState<LeadDispatchLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [selectedLog, setSelectedLog] = useState<LeadDispatchLog | null>(null);

  const fetchLogs = useCallback(
    async (opts: { append?: boolean; cursor?: string | null } = {}) => {
      const { append = false, cursor: cursorParam = null } = opts;
      if (append) setLoadingMore(true);
      else setLoading(true);
      setError(null);
      try {
        const params: Record<string, string | number> = { limit: PAGE_LIMIT };
        if (statusFilter !== "all") params.status = statusFilter;
        if (cursorParam) params.cursor = cursorParam;

        const res = await apiClient.get(
          `/v1/lead-dispatch/rules/${ruleId}/logs`,
          { params }
        );
        const payload: LeadDispatchLogsResponse = res.data?.data ?? {
          logs: [],
        };
        const newLogs = payload.logs ?? [];
        setLogs((prev) => (append ? [...prev, ...newLogs] : newLogs));
        setCursor(payload.nextCursor ?? null);
      } catch (e) {
        console.error(e);
        setError("Errore nel caricamento dei log");
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [ruleId, statusFilter]
  );

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleStatusChange = (e: SelectChangeEvent<StatusFilter>) => {
    setStatusFilter(e.target.value as StatusFilter);
  };

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: 1.5 }}
        flexWrap="wrap"
      >
        <Typography variant="subtitle1" fontWeight={700}>
          Attività recente
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="ld-log-status-filter">Status</InputLabel>
            <Select
              labelId="ld-log-status-filter"
              label="Status"
              value={statusFilter}
              onChange={handleStatusChange}
              disabled={loading || loadingMore}
            >
              <MenuItem value="all">Tutti</MenuItem>
              <MenuItem value="dispatched">Inviato</MenuItem>
              <MenuItem value="duplicate">Duplicato</MenuItem>
              <MenuItem value="skipped">Saltato</MenuItem>
              <MenuItem value="error">Errore</MenuItem>
              <MenuItem value="failed">Fallito</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title="Ricarica">
            <span>
              <IconButton
                size="small"
                onClick={() => fetchLogs()}
                disabled={loading || loadingMore}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : logs.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          Nessun evento registrato per questa regola.
        </Typography>
      ) : (
        <>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Data/ora</TableCell>
                  <TableCell width={120}>Status</TableCell>
                  <TableCell width={80}>HTTP</TableCell>
                  <TableCell width={100}>Durata</TableCell>
                  <TableCell>Simple</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map((log) => {
                  const status = STATUS_CONFIG[log.status];
                  return (
                    <TableRow
                      key={log.id}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => setSelectedLog(log)}
                    >
                      <TableCell>
                        <Typography variant="caption" fontFamily="monospace">
                          {new Date(log.created_at).toLocaleString("it-IT")}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={status?.label ?? log.status}
                          size="small"
                          color={status?.color ?? "default"}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {log.http_status ? (
                          <Chip
                            label={log.http_status}
                            size="small"
                            variant="outlined"
                            color={log.http_status >= 400 ? "error" : "success"}
                            sx={{ fontFamily: "monospace" }}
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" fontFamily="monospace">
                          {log.duration_ms ? `${log.duration_ms}ms` : "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="caption"
                          fontFamily="monospace"
                          color="text.secondary"
                        >
                          {log.simple_rule_id ?? "—"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {cursor && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Button
                onClick={() => fetchLogs({ append: true, cursor })}
                disabled={loadingMore}
                startIcon={
                  loadingMore ? <CircularProgress size={14} /> : undefined
                }
              >
                {loadingMore ? "Caricamento..." : "Carica altri"}
              </Button>
            </Box>
          )}
        </>
      )}

      <LeadDispatchLogDetail
        log={selectedLog}
        onClose={() => setSelectedLog(null)}
      />
    </Box>
  );
}
